// URL-backed range filter for the host detail page.
//
// Presets (1h / 6h / 24h / 7d / 30d) serialize as `?range=24h`. Custom
// windows serialize as `?from=...&to=...` (ISO). Any other combination
// falls back to the default (`24h`) so a stale or hand-edited URL can
// never hard-crash the page.

import { useCallback, useMemo } from "react";
import { useLocation, useSearch } from "wouter";

export type FleetRangePreset = "1h" | "6h" | "24h" | "7d" | "30d";

export interface FleetRangePresetValue {
	kind: "preset";
	preset: FleetRangePreset;
	from: Date;
	to: Date;
	/** True when `to` is "now" (i.e. the chart should auto-refetch). */
	live: true;
	label: string;
}

export interface FleetRangeCustomValue {
	kind: "custom";
	from: Date;
	to: Date;
	live: boolean;
	label: string;
}

export type FleetRangeValue = FleetRangePresetValue | FleetRangeCustomValue;

export const FLEET_RANGE_PRESETS: ReadonlyArray<FleetRangePreset> = [
	"1h",
	"6h",
	"24h",
	"7d",
	"30d",
];

const PRESET_MS: Record<FleetRangePreset, number> = {
	"1h": 60 * 60_000,
	"6h": 6 * 60 * 60_000,
	"24h": 24 * 60 * 60_000,
	"7d": 7 * 24 * 60 * 60_000,
	"30d": 30 * 24 * 60 * 60_000,
};

const PRESET_LABELS: Record<FleetRangePreset, string> = {
	"1h": "last hour",
	"6h": "last 6 hours",
	"24h": "last 24 hours",
	"7d": "last 7 days",
	"30d": "last 30 days",
};

const DEFAULT_PRESET: FleetRangePreset = "24h";

function isPreset(value: string): value is FleetRangePreset {
	return (FLEET_RANGE_PRESETS as ReadonlyArray<string>).includes(value);
}

function parseRange(search: string): FleetRangeValue {
	const params = new URLSearchParams(search);
	const from = params.get("from");
	const to = params.get("to");
	if (from && to) {
		const f = new Date(from);
		const t = new Date(to);
		if (!Number.isNaN(f.getTime()) && !Number.isNaN(t.getTime()) && f < t) {
			return {
				kind: "custom",
				from: f,
				to: t,
				live: false,
				label: formatCustomLabel(f, t),
			};
		}
	}
	const presetParam = params.get("range");
	const preset =
		presetParam && isPreset(presetParam) ? presetParam : DEFAULT_PRESET;
	const now = new Date();
	return {
		kind: "preset",
		preset,
		from: new Date(now.getTime() - PRESET_MS[preset]),
		to: now,
		live: true,
		label: PRESET_LABELS[preset],
	};
}

function formatCustomLabel(from: Date, to: Date): string {
	const opts: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
	};
	const fromStr = from.toLocaleDateString(undefined, opts);
	const toStr = to.toLocaleDateString(undefined, opts);
	return fromStr === toStr ? fromStr : `${fromStr} – ${toStr}`;
}

export interface UseFleetRangeResult {
	range: FleetRangeValue;
	setPreset: (preset: FleetRangePreset) => void;
	setCustom: (from: Date, to: Date) => void;
	reset: () => void;
}

export function useFleetRange(): UseFleetRangeResult {
	const search = useSearch();
	const [location, setLocation] = useLocation();

	const range = useMemo(() => parseRange(search), [search]);

	const replaceParams = useCallback(
		(mutate: (params: URLSearchParams) => void) => {
			const params = new URLSearchParams(search);
			mutate(params);
			const qs = params.toString();
			setLocation(qs ? `${location}?${qs}` : location, { replace: true });
		},
		[search, location, setLocation],
	);

	const setPreset = useCallback(
		(preset: FleetRangePreset) => {
			replaceParams((p) => {
				p.delete("from");
				p.delete("to");
				if (preset === DEFAULT_PRESET) {
					p.delete("range");
				} else {
					p.set("range", preset);
				}
			});
		},
		[replaceParams],
	);

	const setCustom = useCallback(
		(from: Date, to: Date) => {
			replaceParams((p) => {
				p.delete("range");
				p.set("from", from.toISOString());
				p.set("to", to.toISOString());
			});
		},
		[replaceParams],
	);

	const reset = useCallback(() => {
		replaceParams((p) => {
			p.delete("range");
			p.delete("from");
			p.delete("to");
		});
	}, [replaceParams]);

	return { range, setPreset, setCustom, reset };
}
